import { computed, inject, Injectable, resource, signal } from '@angular/core';
import {
  createInfiniteScroll,
  keepLastResolvedValue,
  toInfiniteScrollCollectionVm,
  type IInfiniteScrollCollection,
  type InfiniteScrollRefreshSource,
} from '@okcargo-apps/layout-ionic';
import { DecaDocumentCategory, IDecaDocument } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import {
  ConnectivityService,
  DecaQueryService,
  FieldNames,
  FilterCombinator,
  FilterComparator,
  FilterGroupPayload,
  FindOptions,
  normalizeFilterCombinators,
  OrderType,
  SearchOptionsInput,
  SearchResult,
  type FilterOptions,
} from '@okcargo-apps/shared-infrastructure';
import { endOfDay, startOfDay } from 'date-fns';
import { AccountService } from '../account';
import { IDecaDetailedVM, IDecaFilters, IDecaListPeriod, IDecaListQuery } from './deca-store.model';

@Injectable({
  providedIn: 'root',
})
export class DecaStoreService {
  private readonly decaQueryService = inject(DecaQueryService);
  private readonly accountService = inject(AccountService);
  private readonly connectivity = inject(ConnectivityService);

  private readonly PAGE_SIZE = 20;
  private readonly TODAY_PAGE_SIZE = 300;

  private readonly _selectedDocumentId = signal<string | null>(null);

  // User filters, scoped per period (upcoming/past/today) so a search only affects the
  // collection being viewed. They are part of the query identity.
  private readonly _filters = signal<Record<IDecaListPeriod, IDecaFilters | null>>({
    upcoming: null,
    past: null,
    today: null,
  });

  private canViewAsShipper(): boolean {
    return this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER);
  }

  private canViewAsCarrier(): boolean {
    return (
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER) ||
      this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_DRIVER)
    );
  }

  private decaQueryFor(role: 'shipper' | 'carrier', period: IDecaListPeriod): IDecaListQuery | null {
    const canView = role === 'shipper' ? this.canViewAsShipper() : this.canViewAsCarrier();
    if (!canView) return null;
    return { referenceDate: startOfDay(new Date()).toISOString(), uiFilters: this._filters()[period] };
  }

  // Per-period queries for the upcoming/past collections.
  private readonly decaQueryAsShipperUpcoming = computed<IDecaListQuery | null>(() =>
    this.decaQueryFor('shipper', 'upcoming'),
  );

  private readonly decaQueryAsShipperPast = computed<IDecaListQuery | null>(() => this.decaQueryFor('shipper', 'past'));

  private readonly decaQueryAsCarrierUpcoming = computed<IDecaListQuery | null>(() =>
    this.decaQueryFor('carrier', 'upcoming'),
  );

  private readonly decaQueryAsCarrierPast = computed<IDecaListQuery | null>(() => this.decaQueryFor('carrier', 'past'));

  private readonly decaQueryAsCarrierToday = computed<IDecaListQuery | null>(() =>
    this.decaQueryFor('carrier', 'today'),
  );

  // Today queries carry no user filters: the plate search targets the viewed list only.
  private readonly todayQueryAsShipper = computed<IDecaListQuery | null>(() => {
    if (!this.canViewAsShipper()) return null;
    return { referenceDate: startOfDay(new Date()).toISOString(), uiFilters: null };
  });

  private readonly todayQueryAsCarrier = computed<IDecaListQuery | null>(() => {
    if (!this.canViewAsCarrier()) return null;
    return { referenceDate: startOfDay(new Date()).toISOString(), uiFilters: null };
  });

  private readonly _availableCreditsParams = computed(() => {
    if (
      !this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_SHIPPER) &&
      !this.accountService.hasCapability(ECapabilities.VIEW_DECA_AS_CARRIER)
    ) {
      return null;
    }
    return this.accountService.account().company.id;
  });

  private readonly _availableCredits = resource({
    params: () => this._availableCreditsParams(),
    loader: async ({ params: id, abortSignal }) => {
      if (!id) return null;

      const options: FindOptions = {
        id,
        abortSignal,
      };

      const response = await this.decaQueryService.findAvailableCredits(options);
      return response;
    },
  });

  private readonly upcomingDocumentsAsShipperCollection = createInfiniteScroll<IDecaListQuery, IDecaDocument>({
    query: this.decaQueryAsShipperUpcoming,
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchDocuments('shipper', 'upcoming', query, pageIndex, pageSize, abortSignal),
    getItemKey: (document) => document.id,
  });

  private readonly upcomingDocumentsAsCarrierCollection = createInfiniteScroll<IDecaListQuery, IDecaDocument>({
    query: this.decaQueryAsCarrierUpcoming,
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchDocuments('carrier', 'upcoming', query, pageIndex, pageSize, abortSignal),
    getItemKey: (document) => document.id,
  });

  private readonly pastDocumentsAsShipperCollection = createInfiniteScroll<IDecaListQuery, IDecaDocument>({
    query: this.decaQueryAsShipperPast,
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchDocuments('shipper', 'past', query, pageIndex, pageSize, abortSignal),
    getItemKey: (document) => document.id,
  });

  private readonly pastDocumentsAsCarrierCollection = createInfiniteScroll<IDecaListQuery, IDecaDocument>({
    query: this.decaQueryAsCarrierPast,
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchDocuments('carrier', 'past', query, pageIndex, pageSize, abortSignal),
    getItemKey: (document) => document.id,
  });

  // Control documents that overlap today, paginated, visible to drivers. The query
  // reuses the carrier path; the page is gated to VIEW_DECA_AS_DRIVER at routing.
  private readonly todayDocumentsAsDriverCollection = createInfiniteScroll<IDecaListQuery, IDecaDocument>({
    query: this.decaQueryAsCarrierToday,
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchTodayDocumentsPaginated('carrier', pageIndex, pageSize, abortSignal),
    getItemKey: (document) => document.id,
  });

  private readonly _todayDocumentsAsShipper = resource({
    params: () => this.todayQueryAsShipper(),
    loader: async ({ params, abortSignal }) => {
      if (!params) return { items: [], totalElements: 0 };
      return this.searchTodayDocuments('shipper', abortSignal);
    },
  });

  private readonly _todayDocumentsAsCarrier = resource({
    params: () => this.todayQueryAsCarrier(),
    loader: async ({ params, abortSignal }) => {
      if (!params) return { items: [], totalElements: 0 };
      return this.searchTodayDocuments('carrier', abortSignal);
    },
  });

  private readonly currentDocument = resource({
    params: () => this._selectedDocumentId(),
    loader: async ({ params: id, abortSignal }) => {
      if (!id) return null;

      const options: FindOptions = {
        id,
        abortSignal,
      };

      const response = await this.decaQueryService.findDeca(options);
      return response;
    },
  });

  // derived state (computed)

  /** True when an action that needs the backend cannot be performed (no connectivity). */
  public readonly actionsDisabled = computed(() => this.connectivity.isOffline());

  /*
   * Keeps the last loaded values when a reload fails (e.g. the connection drops
   * in the middle of a refresh) instead of blanking the view.
   */
  private readonly _lastResolvedCredits = keepLastResolvedValue(
    this._availableCredits.snapshot,
    this._availableCreditsParams,
  );

  private readonly _lastResolvedCurrentDocument = keepLastResolvedValue(
    this.currentDocument.snapshot,
    this._selectedDocumentId,
  );

  public readonly availableCredits = computed(() => this._lastResolvedCredits()?.availableCredits ?? 0);

  public readonly promotionalCredits = computed(() => this._lastResolvedCredits()?.promotionalCredits ?? 0);

  public readonly promotionalCreditsExpireAt = computed(
    () => this._lastResolvedCredits()?.promotionalCreditsExpireAt ?? null,
  );

  public readonly canRequestPromotionalCredits = computed(() => {
    const promotionalCredits = this.promotionalCredits();
    const expireAt = this.promotionalCreditsExpireAt();

    return promotionalCredits === 0 || (!!expireAt && expireAt.getTime() < Date.now());
  });

  public readonly upcomingDocumentsAsShipper = this.upcomingDocumentsAsShipperCollection.vm;

  public readonly upcomingDocumentsAsCarrier = this.upcomingDocumentsAsCarrierCollection.vm;

  public readonly pastDocumentsAsShipper = this.pastDocumentsAsShipperCollection.vm;

  public readonly pastDocumentsAsCarrier = this.pastDocumentsAsCarrierCollection.vm;

  public readonly todayDocumentsAsShipper = computed(() => toInfiniteScrollCollectionVm(this._todayDocumentsAsShipper));

  public readonly todayDocumentsAsCarrier = computed(() => toInfiniteScrollCollectionVm(this._todayDocumentsAsCarrier));

  public readonly todayDocumentsAsDriver = this.todayDocumentsAsDriverCollection.vm;

  public activeFiltersFor(period: IDecaListPeriod): IDecaFilters | null {
    return this._filters()[period];
  }

  public hasActiveFiltersFor(period: IDecaListPeriod): boolean {
    return this._filters()[period] !== null;
  }

  public readonly currentDocumentVM = computed(() => {
    const error = this.currentDocument.error();

    const vm: IDecaDetailedVM = {
      item: this._lastResolvedCurrentDocument() ?? null,
      isLoading: this.currentDocument.isLoading(),
      error: error as Error | undefined,
    };

    return vm;
  });

  // QUERIES, imperative calls that force manual state updates

  public loadNextPageFor(category: DecaDocumentCategory): void {
    this.collectionFor(category)?.loadNextPage();
  }

  public retryFor(category: DecaDocumentCategory): void {
    this.collectionFor(category)?.retry();
  }

  public refreshCollectionAsUser(category: DecaDocumentCategory): void {
    this.collectionFor(category)?.refresh('user');
  }

  public reloadAvailableCredits(): void {
    if (this.connectivity.isOffline()) return;
    this._availableCredits.reload();
  }

  public reloadAvailableCreditsAfterSave(): void {
    setTimeout(() => this.reloadAvailableCredits(), 2000); // 2 sec
  }

  // filters commands

  public applyFilters(filters: IDecaFilters, period: IDecaListPeriod): void {
    this._filters.update((current) => ({ ...current, [period]: filters }));
  }

  public clearFilters(period: IDecaListPeriod): void {
    this._filters.update((current) => ({ ...current, [period]: null }));
  }

  public setSelectedDocumentId(id: string | null): void {
    this._selectedDocumentId.set(id);
  }

  // not in use for now
  public clearSelectedDocumentId(): void {
    this._selectedDocumentId.set(null);
  }

  public reloadCurrentDocument(): void {
    if (this.connectivity.isOffline()) return;
    this.currentDocument.reload();
  }

  public reloadDocumentsAsShipper(source: InfiniteScrollRefreshSource = 'polling'): void {
    if (this.connectivity.isOffline()) return;
    this.upcomingDocumentsAsShipperCollection.refresh(source);
    this.pastDocumentsAsShipperCollection.refresh(source);
    this._todayDocumentsAsShipper.reload();
  }

  public reloadDocumentsAsCarrier(source: InfiniteScrollRefreshSource = 'polling'): void {
    if (this.connectivity.isOffline()) return;
    this.upcomingDocumentsAsCarrierCollection.refresh(source);
    this.pastDocumentsAsCarrierCollection.refresh(source);
    this.todayDocumentsAsDriverCollection.refresh(source);
    this._todayDocumentsAsCarrier.reload();
  }

  public reloadAfterSaveData(): void {
    setTimeout(() => {
      this.reloadDocumentsAsShipper('user');
      this.reloadDocumentsAsCarrier('user');
      this.reloadCurrentDocument();
      this.reloadAvailableCredits();
    }, 2000); // 2 sec
  }

  // Plate criteria are OR-combined inside a native query-processor group following the
  // backoffice convention (outer NONE, group AND, payload [AND, OR]), placed first, so the
  // query becomes (plateA OR plateB) AND date. The without-plate option filters on a null
  // tractor plate with a single IS_NULL filter.
  private buildDecaSearchCriteria(dateCriteria: FilterOptions[], period: IDecaListPeriod): FilterOptions[] {
    const uiFilters = this._filters()[period];
    const hasFilterCriteria = !!uiFilters?.plate || !!uiFilters?.withoutPlate;

    // Without user criteria the dates are sanitized so only the first filter carries
    // no combinator and the rest are AND (relevant when there are several dates, e.g. today).
    if (!hasFilterCriteria) {
      return normalizeFilterCombinators(dateCriteria);
    }

    const dateCriteriaWithAnd = dateCriteria.map((filter) => ({ ...filter, combinator: FilterCombinator.AND }));

    if (uiFilters?.withoutPlate) {
      return [
        {
          field: FieldNames.TRACTOR_PLATE_NUMBER, // DigitalControlDocFieldNames
          comparator: FilterComparator.IS_NULL,
          value: null,
          combinator: FilterCombinator.NONE,
          group: null as unknown as FilterGroupPayload,
        },
        ...dateCriteriaWithAnd,
      ];
    }

    const groupedPlateFilter = {
      combinator: FilterCombinator.NONE,
      group: {
        combinator: FilterCombinator.AND,
        filterPayload: [
          {
            field: FieldNames.TRACTOR_PLATE_NUMBER, // DigitalControlDocFieldNames
            comparator: FilterComparator.ILIKE,
            value: uiFilters.plate!,
            combinator: FilterCombinator.AND,
            group: null as unknown as FilterGroupPayload,
          },
          {
            field: FieldNames.TRAILER_PLATE_NUMBER, // DigitalControlDocFieldNames
            comparator: FilterComparator.ILIKE,
            value: uiFilters.plate!,
            combinator: FilterCombinator.OR,
            group: null as unknown as FilterGroupPayload,
          },
        ],
      },
    } as unknown as FilterOptions;

    return [groupedPlateFilter, ...dateCriteriaWithAnd];
  }

  private collectionFor(category: DecaDocumentCategory): IInfiniteScrollCollection<IDecaDocument> | null {
    switch (category) {
      case 'UPCOMING_DOCUMENTS_AS_SHIPPER':
        return this.upcomingDocumentsAsShipperCollection;
      case 'UPCOMING_DOCUMENTS_AS_CARRIER':
        return this.upcomingDocumentsAsCarrierCollection;
      case 'PAST_DOCUMENTS_AS_SHIPPER':
        return this.pastDocumentsAsShipperCollection;
      case 'PAST_DOCUMENTS_AS_CARRIER':
        return this.pastDocumentsAsCarrierCollection;
      case 'TODAY_DOCUMENTS_AS_DRIVER':
        return this.todayDocumentsAsDriverCollection;
    }
  }

  private searchDocuments(
    role: 'shipper' | 'carrier',
    period: 'upcoming' | 'past',
    _query: IDecaListQuery,
    pageIndex: number,
    pageSize: number,
    abortSignal: AbortSignal,
  ): Promise<SearchResult<IDecaDocument>> {
    const referenceDate = startOfDay(new Date()).toISOString();

    const options: SearchOptionsInput = {
      pageIndex,
      pageSize,
      filters: this.buildDecaSearchCriteria(
        [
          {
            field: FieldNames.LATEST_STARTING_AT, // DecaDocFieldNames
            value: referenceDate,
            comparator: period === 'upcoming' ? FilterComparator.GTE : FilterComparator.LT,
            combinator: FilterCombinator.NONE,
            group: null as unknown as FilterGroupPayload,
          },
        ],
        period,
      ),
      order: [
        {
          field: FieldNames.EARLIEST_STARTING_AT, // DecaDocFieldNames
          type: period === 'upcoming' ? OrderType.ASC : OrderType.DESC,
        },
      ],
      abortSignal,
    };

    return role === 'shipper'
      ? this.decaQueryService.searchDecaAsShipper(options)
      : this.decaQueryService.searchDecaAsCarrier(options);
  }

  private searchTodayDocuments(
    role: 'shipper' | 'carrier',
    abortSignal: AbortSignal,
  ): Promise<SearchResult<IDecaDocument>> {
    const options: SearchOptionsInput = {
      pageIndex: 0,
      pageSize: this.TODAY_PAGE_SIZE,
      filters: this.todayDateCriteria(),
      order: [
        {
          field: FieldNames.EARLIEST_STARTING_AT, // DecaDocFieldNames
          type: OrderType.ASC,
        },
      ],
      abortSignal,
    };

    return role === 'shipper'
      ? this.decaQueryService.searchDecaAsShipper(options)
      : this.decaQueryService.searchDecaAsCarrier(options);
  }

  // Paginated variant used by the today-as-driver collection; the user filters for
  // the 'today' period are combined with the day-overlap criteria.
  private searchTodayDocumentsPaginated(
    role: 'carrier',
    pageIndex: number,
    pageSize: number,
    abortSignal: AbortSignal,
  ): Promise<SearchResult<IDecaDocument>> {
    const options: SearchOptionsInput = {
      pageIndex,
      pageSize,
      filters: this.buildDecaSearchCriteria(this.todayDateCriteria(), 'today'),
      order: [
        {
          field: FieldNames.EARLIEST_STARTING_AT, // DecaDocFieldNames
          type: OrderType.ASC,
        },
      ],
      abortSignal,
    };

    return this.decaQueryService.searchDecaAsCarrier(options);
  }

  // Criteria for documents overlapping today: first segment starts before end of day
  // and last segment ends after start of day.
  private todayDateCriteria(): FilterOptions[] {
    return [
      {
        field: FieldNames.EARLIEST_STARTING_AT, // DecaDocFieldNames
        value: endOfDay(new Date()).toISOString(),
        comparator: FilterComparator.LT,
        combinator: FilterCombinator.NONE,
        group: null as unknown as FilterGroupPayload,
      },
      {
        field: FieldNames.LATEST_STARTING_AT, // DecaDocFieldNames
        value: startOfDay(new Date()).toISOString(),
        comparator: FilterComparator.GT,
        combinator: FilterCombinator.AND,
        group: null as unknown as FilterGroupPayload,
      },
    ];
  }
}
